import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  GridLegacy as Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@mui/material';

import CategoryIconWidget from '~/components/Common/Forms/widgets/CategoryIconWidget';
import Spinner from '~/components/Common/Spinner';
import {
  CruiseInclusion,
  CruiseInclusionItem,
  CruiseInclusionItemCategory,
  CruiseInclusionItemDisplayType,
} from '~/components/Cruises/pages/Inclusions/types';

import useAppNavigation from '~/hooks/useAppNavigation';

import inclusionsService from '~/services/cruises/InclusionsService';

const TYPES: { [key: string]: CruiseInclusionItem['type'] } = {
  FREE_TEXT: 'FREE_TEXT',
  ONBOARD_CREDIT: 'ONBOARD_CREDIT',
};

export const ONBOARD_CREDIT_ICON = 'dollar_sign_in_circle';

const DISPLAY_TYPES: { [key: string]: CruiseInclusionItemDisplayType } = {
  BENEFIT: 'BENEFIT',
  BOOKING_CONDITION: 'BOOKING_CONDITION',
};

const INCLUSION_ITEM_CATEGORIES: Array<{ label: string; value: CruiseInclusionItemCategory }> = [
  { label: 'Flight Credit', value: 'FLIGHT_CREDIT' },
];

const INITIAL_FORM_STATE: Partial<CruiseInclusionItem> = {
  categories: [],
  description: '',
  type: TYPES.ONBOARD_CREDIT,
  creditCurrency: undefined,
  creditAmount: 0,
  icon: ONBOARD_CREDIT_ICON,
  isHighlighted: false,
};

const buildInitialFormState = (inclusionType: CruiseInclusion['type']): Partial<CruiseInclusionItem> => {
  if (inclusionType === 'EVERGREEN') {
    return {
      ...INITIAL_FORM_STATE,
      type: TYPES.FREE_TEXT,
      icon: undefined,
    };
  }

  if (inclusionType === 'STANDARD') {
    return {
      ...INITIAL_FORM_STATE,
      displayType: DISPLAY_TYPES.BENEFIT,
    };
  }

  return INITIAL_FORM_STATE;
};

type Props = {
  inclusionId: string;
  inclusionItem?: CruiseInclusionItem;
  inclusionType: CruiseInclusion['type'];
  onSave: () => void;
};

function InclusionItemForm({ inclusionId, inclusionItem, inclusionType, onSave }: Props) {
  const { push } = useAppNavigation();
  const [formValues, setFormValues] = useState<Partial<CruiseInclusionItem>>(() =>
    buildInitialFormState(inclusionType),
  );
  const [status, setStatus] = useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = !!inclusionItem;
  const isEvergreenInclusion = inclusionType === 'EVERGREEN';
  const isStandardInclusion = inclusionType === 'STANDARD';
  const isBookingCondition = formValues.displayType === DISPLAY_TYPES.BOOKING_CONDITION;
  const { control, setValue } = useForm({
    defaultValues: {
      icon: '',
    },
  });

  const handleChangeField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value ?? undefined,
      });
    },
    [formValues],
  );

  const handleChangeNumberField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      setFormValues({
        ...formValues,
        [event.target.name]: parseFloat(event.target.value),
      });
    },
    [formValues],
  );

  const handleSelectChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        setFormValues({
          ...formValues,
          [event.target.name]: event.target.value,
        });
      }
    },
    [formValues],
  );

  const handleChangeIcon = useCallback(
    (name: string, value: unknown, config?: object) => {
      setValue(name as 'icon', value as string, config);
      setFormValues({
        ...formValues,
        icon: (value as string) || undefined,
      });
    },
    [formValues, setValue],
  );

  const handleCategoryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const category = event.target.value as CruiseInclusionItemCategory;

    setFormValues((currentValues) => {
      const categories = currentValues.categories ?? [];

      return {
        ...currentValues,
        categories: event.target.checked
          ? [...categories, category]
          : categories.filter((currentCategory) => currentCategory !== category),
      };
    });
  }, []);

  const handleChangeTab = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();

      const type = event.target.value as CruiseInclusionItem['type'];
      const icon = type === TYPES.ONBOARD_CREDIT ? ONBOARD_CREDIT_ICON : undefined;

      setValue('icon', icon ?? '');
      setFormValues({
        ...buildInitialFormState(inclusionType),
        [event.target.name]: type,
        icon,
      });
    },
    [inclusionType, setValue],
  );

  const handleDisplayTypeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const displayType = event.target.value as CruiseInclusionItemDisplayType;
      const isBookingCondition = displayType === DISPLAY_TYPES.BOOKING_CONDITION;

      if (isBookingCondition) {
        setValue('icon', '');
      }

      setFormValues((currentValues) => ({
        ...currentValues,
        displayType,
        ...(isBookingCondition && {
          type: TYPES.FREE_TEXT,
          categories: [],
          creditAmount: undefined,
          creditCurrency: undefined,
          icon: undefined,
        }),
      }));
    },
    [setValue],
  );

  const handleSubmit = useCallback(async () => {
    setStatus('loading');

    try {
      const isBookingCondition = formValues.displayType === DISPLAY_TYPES.BOOKING_CONDITION;
      const { displayType: _displayType, ...inclusionItemValues } = formValues;
      const payload = {
        ...inclusionItemValues,
        ...(isStandardInclusion && {
          displayType: formValues.type === TYPES.ONBOARD_CREDIT ? DISPLAY_TYPES.BENEFIT : formValues.displayType,
        }),
        type: isBookingCondition ? TYPES.FREE_TEXT : formValues.type,
        categories: isBookingCondition ? [] : formValues.categories,
        creditAmount: isBookingCondition ? undefined : formValues.creditAmount,
        creditCurrency: isBookingCondition ? undefined : formValues.creditCurrency,
        icon: isBookingCondition
          ? undefined
          : formValues.type === TYPES.ONBOARD_CREDIT
            ? ONBOARD_CREDIT_ICON
            : formValues.icon,
      };

      if (isEditing) {
        await inclusionsService.updateItem(inclusionId, inclusionItem.id, payload);
      } else {
        await inclusionsService.createItem(inclusionId, payload);
      }

      setStatus('success');
      enqueueSnackbar('Inclusion Item saved with success', { variant: 'success' });
      onSave();
      push(`/cruises/inclusions/edit/${inclusionId}/items`);
    } catch (error) {
      console.error(error);
      setStatus('error');
      enqueueSnackbar('Error saving Inclusion Item', { variant: 'error' });
    }
  }, [formValues, isEditing, isStandardInclusion, push, enqueueSnackbar, inclusionId, inclusionItem, onSave]);

  const handleCancel = useCallback(() => {
    push(`/cruises/inclusions/edit/${inclusionId}/items`);
  }, [push, inclusionId]);

  const isSaveEnabled = useMemo(() => {
    const isLoading = status === 'loading';
    if (isBookingCondition) {
      return !isLoading && formValues.description;
    }

    if (formValues.type === TYPES.ONBOARD_CREDIT) {
      return !isLoading && formValues.creditCurrency && formValues.creditAmount;
    }

    return !isLoading && formValues.description;
  }, [formValues, isBookingCondition, status]);

  useEffect(() => {
    if (inclusionItem) {
      setFormValues({
        type: inclusionItem.type,
        categories: inclusionItem.categories ?? [],
        ...(isStandardInclusion && { displayType: inclusionItem.displayType ?? DISPLAY_TYPES.BENEFIT }),
        description: inclusionItem.description,
        creditCurrency: inclusionItem.creditCurrency,
        creditAmount: inclusionItem.creditAmount,
        icon: inclusionItem.icon ?? (inclusionItem.type === TYPES.ONBOARD_CREDIT ? ONBOARD_CREDIT_ICON : undefined),
      });
      setValue('icon', inclusionItem.icon ?? '');
    }
  }, [inclusionItem, isStandardInclusion, setValue]);

  return (
    <Box>
      {!isEditing && !isBookingCondition && (
        <Box>
          <FormControl>
            <FormLabel>Type</FormLabel>
            <RadioGroup
              defaultValue={TYPES.FREE_TEXT}
              name="type"
              value={formValues.type}
              onChange={handleChangeTab}
              row
            >
              {!isEvergreenInclusion && (
                <FormControlLabel value={TYPES.ONBOARD_CREDIT} control={<Radio />} label="Onboard Credit" />
              )}
              <FormControlLabel value={TYPES.FREE_TEXT} control={<Radio />} label="Free Text" />
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      {!isBookingCondition && formValues.type === TYPES.ONBOARD_CREDIT && (
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box mt={2}>
              <FormControl fullWidth>
                <TextField
                  select
                  name="creditCurrency"
                  label="Currency"
                  onChange={handleSelectChange}
                  value={formValues.creditCurrency}
                  key={formValues.creditCurrency}
                >
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </TextField>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={9}>
            <Box mt={2}>
              <TextField
                fullWidth
                type="number"
                name="creditAmount"
                label="Amount"
                onChange={handleChangeNumberField}
                value={formValues.creditAmount}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {(isBookingCondition || formValues.type === TYPES.FREE_TEXT) && (
        <>
          {!isBookingCondition && (
            <Box mt={2}>
              <CategoryIconWidget control={control} label="Icon" name="icon" setValue={handleChangeIcon} />
            </Box>
          )}

          <Box mt={2}>
            <TextField
              required
              fullWidth
              name="description"
              label="Description"
              onChange={handleChangeField}
              inputProps={{ maxLength: 300 }}
              value={formValues.description}
              helperText="The maximum length is 300 characters."
            />
          </Box>
        </>
      )}

      {isStandardInclusion && formValues.type === TYPES.FREE_TEXT && (
        <Box mt={2}>
          <FormControl>
            <FormLabel>Display Type</FormLabel>
            <RadioGroup name="displayType" value={formValues.displayType} onChange={handleDisplayTypeChange} row>
              <FormControlLabel
                value={DISPLAY_TYPES.BENEFIT}
                control={<Radio />}
                label={
                  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                    Benefit (Inclusion Item)
                    <Tooltip title="Shows as an inclusion item on customer-facing cruise surfaces.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
              />
              <FormControlLabel
                value={DISPLAY_TYPES.BOOKING_CONDITION}
                control={<Radio />}
                label={
                  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                    Booking condition (Inclusion Tooltip)
                    <Tooltip title="Shows in the inclusion tooltip instead of the visible inclusion list.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      {isStandardInclusion && !isBookingCondition && formValues.type === TYPES.FREE_TEXT && (
        <Box mt={2}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Categories</FormLabel>
            <FormGroup row>
              {INCLUSION_ITEM_CATEGORIES.map((category) => (
                <FormControlLabel
                  key={category.value}
                  label={category.label}
                  control={
                    <Checkbox
                      checked={formValues.categories?.includes(category.value) ?? false}
                      value={category.value}
                      onChange={handleCategoryChange}
                    />
                  }
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>
      )}

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {status === 'loading' && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default InclusionItemForm;
